import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Badge } from 'react-bootstrap';
import api from '../lib/api';
import { useToast } from './common/ToastProvider';

const emptyChatUi = {
  greeting_text: '',
  about_text: '',
  input_placeholder: '',
  suggestions: [],
  greeting_patterns: [],
  about_patterns: [],
  handover_request_text: '',
  handover_pending_text: '',
  handover_assigned_text: '',
  handover_agent_unavailable_text: '',
  handover_resolved_text: '',
};

function SettingsBlock({ title, subtitle, children, className = '' }) {
  return (
    <Card
      className={`border-0 rounded-4 h-100 ${className}`}
      style={{ background: '#f8fafc' }}
    >
      <Card.Body className="p-3 p-md-4">
        <div className="fw-semibold mb-1" style={{ color: '#0f172a', fontSize: '1rem' }}>
          {title}
        </div>
        {subtitle ? (
          <div className="text-muted small mb-3" style={{ lineHeight: 1.55 }}>
            {subtitle}
          </div>
        ) : null}
        {children}
      </Card.Body>
    </Card>
  );
}

function normalizeTag(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function TagInput({
  label,
  placeholder,
  values = [],
  onChange,
  rows = 2,
}) {
  const [input, setInput] = useState('');

  const addTag = (raw) => {
    const next = normalizeTag(raw);
    if (!next) return;

    const exists = values.some((v) => v.toLowerCase() === next.toLowerCase());
    if (exists) return;

    onChange([...values, next]);
  };

  const removeTag = (idx) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (input.trim()) {
        addTag(input);
        setInput('');
      }
    } else if (e.key === 'Backspace' && !input && values.length) {
      removeTag(values.length - 1);
    }
  };

  const handleBlur = () => {
    if (input.trim()) {
      addTag(input);
      setInput('');
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!text) return;

    const parts = text
      .split(/[\n,]+/)
      .map(normalizeTag)
      .filter(Boolean);

    if (parts.length <= 1) return;

    e.preventDefault();

    const existing = new Set(values.map((v) => v.toLowerCase()));
    const merged = [...values];

    parts.forEach((part) => {
      const key = part.toLowerCase();
      if (!existing.has(key)) {
        existing.add(key);
        merged.push(part);
      }
    });

    onChange(merged);
    setInput('');
  };

  const helperText = useMemo(() => {
    if (!values.length) return 'Press Enter or comma to add each item.';
    return `${values.length} item${values.length > 1 ? 's' : ''} added. Press Enter or comma to add more.`;
  }, [values]);

  return (
    <Form.Group>
      <Form.Label className="mb-2">{label}</Form.Label>

      <div
        className="border rounded-4 bg-white px-3 py-3"
        style={{
          minHeight: rows * 48 + 22,
          borderColor: '#dee2e6',
        }}
      >
        <div className="d-flex flex-wrap gap-2 mb-2">
          {values.map((item, idx) => (
            <span
              key={`${item}-${idx}`}
              className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
              style={{
                background: 'rgba(13, 110, 253, 0.08)',
                color: '#0d6efd',
                fontSize: '0.9rem',
                lineHeight: 1,
              }}
            >
              <span>{item}</span>
              <button
                type="button"
                onClick={() => removeTag(idx)}
                aria-label={`Remove ${item}`}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: '#0d6efd',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <Form.Control
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="border-0 shadow-none px-0"
          style={{
            background: 'transparent',
            minHeight: 34,
          }}
        />
      </div>

      <div className="text-muted small mt-2">{helperText}</div>
    </Form.Group>
  );
}

export default function AdminChatUiSettingsCard() {
  const [chatUi, setChatUi] = useState(emptyChatUi);
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/chat-instructions/chat-ui/');
      setChatUi({ ...emptyChatUi, ...(data || {}) });
      setStatus('');
    } catch (e) {
      const message = e?.response?.data?.error || 'Failed to load AI chat settings.';
      setStatus(message);
      setStatusType('danger');
      showToast(message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      await api.post('/chat-instructions/chat-ui/', chatUi);
      setStatus('AI chat settings saved.');
      setStatusType('success');
      showToast('AI chat settings saved.', 'success');
    } catch (e) {
      const message = e?.response?.data?.error || 'Failed to save AI chat settings.';
      setStatus(message);
      setStatusType('danger');
      showToast(message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const suggestionCount = useMemo(() => (chatUi.suggestions || []).length, [chatUi.suggestions]);
  const greetingPatternCount = useMemo(
    () => (chatUi.greeting_patterns || []).length,
    [chatUi.greeting_patterns]
  );
  const aboutPatternCount = useMemo(
    () => (chatUi.about_patterns || []).length,
    [chatUi.about_patterns]
  );

  if (loading) {
    return (
      <Card className="admin-surface-card border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <div className="admin-section-title mb-0">AI Chat Settings</div>
          <div className="text-muted small mt-2">Loading settings...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="admin-surface-card border-0 shadow-sm rounded-4 overflow-hidden">
      <Card.Body className="p-4 p-lg-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div className="admin-section-title mb-1">AI Chat Settings</div>
            <p className="text-muted small mb-0" style={{ maxWidth: 780 }}>
              Configure the visible chat copy, greeting logic, suggestion prompts, and handover responses
              used by the assistant at runtime.
            </p>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Badge bg="light" text="dark" pill className="px-3 py-2 border">
              Suggestions: {suggestionCount}
            </Badge>
            <Badge bg="light" text="dark" pill className="px-3 py-2 border">
              Greeting Triggers: {greetingPatternCount}
            </Badge>
            <Badge bg="light" text="dark" pill className="px-3 py-2 border">
              About Triggers: {aboutPatternCount}
            </Badge>
          </div>
        </div>

        {status ? (
          <Alert variant={statusType} className="rounded-4">
            {status}
          </Alert>
        ) : null}

        <Row className="g-4">
          <Col xl={6}>
            <SettingsBlock
              title="Core Chat Text"
              subtitle="Main copy shown in the chat window. Keep this clear, welcoming, and aligned with your brand tone."
            >
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Greeting Text</Form.Label>
                    <Form.Control
                      value={chatUi.greeting_text || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, greeting_text: e.target.value })
                      }
                      placeholder="Example: Hello. Welcome to our support desk. How can I help you today?"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>About / Identity Text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={chatUi.about_text || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, about_text: e.target.value })
                      }
                      placeholder="Example: I’m your AI Agent. I can help with products, orders, billing, and general questions."
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Input Placeholder</Form.Label>
                    <Form.Control
                      value={chatUi.input_placeholder || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, input_placeholder: e.target.value })
                      }
                      placeholder="Example: Ask about pricing, support, or your order..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SettingsBlock>
          </Col>

          <Col xl={6}>
            <SettingsBlock
              title="Handover Messaging"
              subtitle="These messages are shown for pending, connected, unavailable, and resolved handover states."
            >
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Handover Request Trigger</Form.Label>
                    <Form.Control
                      value={chatUi.handover_request_text || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, handover_request_text: e.target.value })
                      }
                      placeholder="Example: I want to talk to a real person"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Pending Response Text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={chatUi.handover_pending_text || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, handover_pending_text: e.target.value })
                      }
                      placeholder="Example: Your handover request has been received. Please wait while we connect you to an available agent."
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Agent Connected Text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={chatUi.handover_assigned_text || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, handover_assigned_text: e.target.value })
                      }
                      placeholder="Example: You are now connected to {agent_name}. They will assist you shortly."
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Agent Unavailable Text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={chatUi.handover_agent_unavailable_text || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, handover_agent_unavailable_text: e.target.value })
                      }
                      placeholder="Example: All our agents are currently unavailable. Please try again shortly."
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Handover Resolved Text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={chatUi.handover_resolved_text || ''}
                      onChange={(e) =>
                        setChatUi({ ...chatUi, handover_resolved_text: e.target.value })
                      }
                      placeholder="Example: Your handover has been resolved. If needed, you can start a new request."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SettingsBlock>
          </Col>

          <Col xl={6}>
            <SettingsBlock
              title="Greeting Trigger Phrases"
              subtitle="Add phrases one by one. Press Enter or comma after each phrase for faster input."
            >
              <TagInput
                label="Greeting Patterns"
                values={chatUi.greeting_patterns || []}
                onChange={(values) => setChatUi({ ...chatUi, greeting_patterns: values })}
                placeholder="Examples: hello, hi, good morning, hey there"
                rows={4}
              />
            </SettingsBlock>
          </Col>

          <Col xl={6}>
            <SettingsBlock
              title="About Trigger Phrases"
              subtitle="Add phrases that indicate users want to know who the assistant is or what it does."
            >
              <TagInput
                label="About Patterns"
                values={chatUi.about_patterns || []}
                onChange={(values) => setChatUi({ ...chatUi, about_patterns: values })}
                placeholder="Examples: who are you, what do you do, tell me about yourself"
                rows={4}
              />
            </SettingsBlock>
          </Col>

          <Col xl={12}>
            <SettingsBlock
              title="Suggestion Prompts"
              subtitle="Add suggestion prompts one by one. Press Enter or comma to turn each into a tag."
            >
              <TagInput
                label="Suggestions"
                values={chatUi.suggestions || []}
                onChange={(values) => setChatUi({ ...chatUi, suggestions: values })}
                placeholder="Examples: What services do you offer?, How can I get started?, Can I talk to support?"
                rows={3}
              />
            </SettingsBlock>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mt-4">
          <Button
            className="admin-action-btn px-4 py-2 rounded-3"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save AI Chat Settings'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
